import { http } from "../lib/http";
import type { LineItem, MatchPair } from "../types/items";

type DiffResponse = {
  ok: boolean;
  stats?: { missing: number; extra: number; qtyVariance: number };
  missing: LineItem[];
  extra: LineItem[];
  qtyVariance: Array<{ code?: string; carrierQty: number; insuredQty: number }>;
};

const key = (it?: LineItem) =>
  ((it?.code || "") + "::" + (it?.description || ""))
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

export async function diffServer(carrierItems: LineItem[], insuredItems: LineItem[]) {
  return http
    .post("diff", {
      json: {
        carrierItems,
        insuredItems,
      },
    })
    .json<DiffResponse>();
}

export function buildPairsFromServer(
  carrierItems: LineItem[],
  insuredItems: LineItem[],
  diff: DiffResponse,
): MatchPair[] {
  const pairs: MatchPair[] = [];
  const missingSet = new Set((diff.missing || []).map((item) => key(item)));
  const extraSet = new Set((diff.extra || []).map((item) => key(item)));

  const rightIndex = new Map<string, LineItem>();
  insuredItems.forEach((item) => rightIndex.set(key(item), item));

  const usedRight = new Set<string>();
  carrierItems.forEach((leftItem) => {
    const k = key(leftItem);
    if (missingSet.has(k)) {
      pairs.push({ left: leftItem, status: "missing" });
      return;
    }

    const rightItem = rightIndex.get(k);
    if (rightItem) {
      usedRight.add(k);
      const qtyMatches = Number(leftItem.qty ?? 0) === Number(rightItem.qty ?? 0);
      pairs.push({
        left: leftItem,
        right: rightItem,
        status: qtyMatches ? "matched" : "qty-variance",
      });
      return;
    }

    pairs.push({ left: leftItem, status: "missing" });
  });

  insuredItems.forEach((rightItem) => {
    const k = key(rightItem);
    if (!usedRight.has(k) && extraSet.has(k)) {
      pairs.push({ right: rightItem, status: "extra" });
    }
  });

  return pairs;
}
